import React from 'react';
import { useState, useEffect } from 'react';
import CkEditor from './CkEditor';
import './classes/EditBlog.css';
import './classes/DrupalEditForm.css';
import { configData } from "../config";
import Loading from './Loading';
import { URL_RESTRICTIONS, urlRestrictionChoices, urlRestrictionLabels } from './urlRestriction';

const errorIcon = '#fef5f1 url("/basic/web/images/message-24-error.png") left 10px top 10px no-repeat';
const grippie = { background: '#eee url("/basic/web/images/grippie.png") no-repeat center 2px' };

// The trimmed version Drupal's preview shows (text_summary() in its text module): the summary
// when there is one, else the body up to <!--break-->, else its first 600 characters cut back to
// the end of a paragraph, line or sentence, with the tags the cut left open closed again.
// A body with PHP is not cut, as Drupal's PHP format does not cut it.
const TRIM_LENGTH = 600;
const BREAK_POINTS = [
    // [text, characters of it to drop], best first
    [['</p>', 0]],
    [['<br />', 6], ['<br>', 4]],
    [['. ', 1], ['! ', 1], ['? ', 1]],
];
// Where the last of a group's break points in text ends, or -1
const lastBreak = (text, points) => Math.max(...points.map(([point, drop]) => {
    const at = text.lastIndexOf(point);
    return at === -1 ? -1 : at + point.length - drop;
}));
const trimmedVersion = (summary, body) => {
    if (summary.trim()) return summary;
    const delimiter = body.indexOf('<!--break-->');
    if (delimiter !== -1) return body.slice(0, delimiter);
    if (body.length <= TRIM_LENGTH || body.includes('<?')) return body;
    let text = body.slice(0, TRIM_LENGTH);
    for (const points of BREAK_POINTS) {
        const end = lastBreak(text, points);
        if (end > 0) {
            text = text.slice(0, end);
            break;
        }
    }
    const corrector = document.createElement('template');
    corrector.innerHTML = text;
    return corrector.innerHTML;
};

// Form Switch's choices. "Registration Form" is the one live's code checks for (smww.module puts
// the registration form in place of {{REGISTRATION}} on the referral and Doc Talk pages); a
// stored value that is not in the list is offered as well, so saving keeps it.
const FORM_SWITCHES = ['Registration Form'];

// The fields "View changes" compares, by their labels on the form
const DIFF_FIELDS = [
    ['Title', 'title'],
    ['Summary', 'body_summary'],
    ['Body', 'body'],
    ['Meta Title', 'field_meta_title'],
    ['Meta Keywords', 'field_meta_keywords'],
    ['Meta Description', 'field_meta_description'],
    ['URL Restriction', 'field_url_restriction'],
    ['Header Image', 'field_header_image'],
    ['Form Switch', 'field_form_switch'],
    ['Parent Course', 'field_parent_course'],
    ['URL alias', 'url_alias'],
];
// A field's value as "View changes" shows it: Parent Course by the course's title (courseTitle)
const diffText = (data, field, courseTitle) => {
    if (field === 'field_url_restriction') return urlRestrictionLabels(data[field]);
    if (field === 'field_parent_course') return courseTitle(data[field]);
    return data[field];
};

// A field's rows in the "View changes" table, as the other edit forms show them: each changed
// line with its words that differ in red
const diffRows = (label, oldValue, newValue) => {
    if (oldValue === newValue) return [];
    const oldLines = oldValue.split('\n');
    const newLines = newValue.split('\n');
    const rows = [
        <tr className="diff-section-title" key={label + '-title'}>
            <td colSpan="4">Changes to <em>{label}</em></td>
        </tr>
    ];
    const renderWords = (words, otherWords, side, line) => words.map((word, index) => (
        <span
            key={label + '-' + side + '-' + line + '-' + index}
            style={word !== (otherWords[index] || '') ? { color: 'red', borderRadius: '4px', padding: '0 3px', fontWeight: 'bold' } : {}}
        >
            {word}{' '}
        </span>
    ));
    for (let i = 0; i < Math.max(oldLines.length, newLines.length); i++) {
        const oldLine = (oldLines[i] || '').trim();
        const newLine = (newLines[i] || '').trim();
        if (oldLine === newLine) continue;
        const oldWords = oldLine.split(/\s+/);
        const newWords = newLine.split(/\s+/);
        rows.push(
            <tr key={label + '-' + i}>
                <td style={{ width: "2px" }}><span>-</span></td>
                <td className="diff-removed">{renderWords(oldWords, newWords, 'old', i)}</td>
                <td style={{ width: "2px" }}><span>+</span></td>
                <td className="diff-added">{renderWords(newWords, oldWords, 'new', i)}</td>
            </tr>
        );
    }
    // Changes to spacing only are not visible changes
    return rows.length > 1 ? rows : [];
};

// The Edit tab of a basic page (Drupal's "Basic page", pages.type "page"), such as the
// Courses and SMWW Apprenticeships pages: its title, body, meta tags, URL Restriction, Header
// Image, Form Switch, Parent Course and address, as live's Drupal form shows them.
// The body's styles, scripts and PHP stay as they are in the rich text editor, which keeps
// them out of its editing area (CkEditor.js).
const EditBasicPage = ({ page }) => {
    const app_url = configData.APP_URL;
    const [formData, setFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [backendErrorMessage, setBackendErrorMessage] = useState('');
    const [showSummary, setShowSummary] = useState(false);
    const [useRichEditor, setUseRichEditor] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [buttonText, setButtonText] = useState('Save');
    // The page as loaded, what "View changes" compares the form with
    const [originalData, setOriginalData] = useState(null);
    // What the Preview button shows, or null
    const [preview, setPreview] = useState(null);
    // The form when "View changes" was pressed, or null
    const [changesSnapshot, setChangesSnapshot] = useState(null);
    // The courses Parent Course offers, [{ nid, title }]: as live's list, the published courses
    // with an application key and a preview tile description, by title (the server picks them)
    const [parentCourses, setParentCourses] = useState([]);

    useEffect(() => {
        getPageData();
    }, [page.nid]);

    // The page as stored: the body here is what was entered, not what the View tab shows
    async function getPageData() {
        try {
            setIsLoading(true);
            const response = await fetch(app_url + 'pages/geteditpagedata?nid=' + page.nid, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });
            const responsedata = await response.json();
            if (responsedata.data) {
                const data = responsedata.data;
                const loaded = {
                    nid: data.nid,
                    title: data.title || '',
                    body: data.body || '',
                    body_summary: data.body_summary || '',
                    field_meta_title: data.field_meta_title || '',
                    field_meta_keywords: data.field_meta_keywords || '',
                    field_meta_description: data.field_meta_description || '',
                    field_url_restriction: urlRestrictionChoices(data.field_url_restriction),
                    field_header_image: data.field_header_image || '',
                    field_form_switch: data.field_form_switch || '',
                    field_parent_course: data.field_parent_course || '',
                    url_alias: data.url_alias || '',
                };
                setFormData(loaded);
                setOriginalData(loaded);
                setParentCourses((data.parent_course_options || []).map((course) => ({ nid: String(course.nid), title: course.title })));
                setShowSummary(!!data.body_summary);
            } else {
                setBackendErrorMessage(responsedata.message || 'The page could not be loaded.');
            }
        } catch (err) {
            setBackendErrorMessage('The page could not be loaded.');
        } finally {
            setIsLoading(false);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // The ticked URL Restriction domains, kept in the checkboxes' order
    const handleUrlRestrictionChange = (e) => {
        const { value, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            field_url_restriction: URL_RESTRICTIONS
                .map((choice) => choice.value)
                .filter((domain) => (domain === value ? checked : prev.field_url_restriction.includes(domain))),
        }));
    };

    // Shows the error and returns false when the required title is missing
    const validate = () => {
        const validationErrors = {};
        if (!formData.title.trim()) {
            validationErrors.title = 'Title field is required.';
        }
        setErrors(validationErrors);
        setBackendErrorMessage('');
        if (Object.keys(validationErrors).length > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return false;
        }
        return true;
    };

    const handlePreviewClick = () => {
        setChangesSnapshot(null);
        if (!validate()) {
            setPreview(null);
            return;
        }
        setPreview({
            ...formData,
            trimmed: trimmedVersion(formData.body_summary, formData.body),
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleViewChangesClick = () => {
        setPreview(null);
        if (!validate()) {
            setChangesSnapshot(null);
            return;
        }
        setChangesSnapshot({ ...formData });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setPreview(null);
        setChangesSnapshot(null);
        if (!validate()) return;

        // URL Restriction goes only when it was changed, so a domain migrated from Drupal is kept
        // as it is stored
        const postData = { ...formData };
        if (urlRestrictionLabels(formData.field_url_restriction) === urlRestrictionLabels(originalData.field_url_restriction)) {
            delete postData.field_url_restriction;
        }

        setDisabled(true);
        setButtonText('Saving...');
        try {
            const response = await fetch(app_url + 'pages/updatepage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(postData),
            });
            const data = await response.json();
            if (data.data?.msg === 'Success') {
                window.location.href = '/' + data.data.url_slug;
                return;
            }
            setBackendErrorMessage(data.data?.text || data.message || 'The page could not be saved.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setBackendErrorMessage('The page could not be saved.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setDisabled(false);
            setButtonText('Save');
        }
    };

    // Drupal's Delete: asks first, then removes the page for good and goes to the front page
    const handleDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete ${formData.title}? This action cannot be undone.`)) return;

        setPreview(null);
        setChangesSnapshot(null);
        setDisabled(true);
        try {
            const response = await fetch(app_url + 'pages/deletepage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ nid: formData.nid }),
            });
            const data = await response.json();
            if (data.data?.msg === 'Success') {
                window.location.href = '/';
                return;
            }
            setBackendErrorMessage(data.data?.text || data.message || 'The page could not be deleted.');
        } catch (err) {
            setBackendErrorMessage('The page could not be deleted.');
        } finally {
            setDisabled(false);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // A Parent Course value as its course's title; a value that is not a listed course, as it is
    const courseTitle = (nid) => parentCourses.find((course) => course.nid === nid)?.title || nid;
    // Parent Course's choices: a stored value that is not a listed course is offered as well, so
    // saving keeps it
    const storedParentCourse = originalData?.field_parent_course;
    const parentCourseChoices = storedParentCourse && !parentCourses.some((course) => course.nid === storedParentCourse)
        ? [...parentCourses, { nid: storedParentCourse, title: storedParentCourse }]
        : parentCourses;

    if (isLoading) return <Loading />;

    return (
        <div id="main" className="edit-blog-main drupal-edit-page">
            <div className="left"></div>
            <div className="center left-center">
                {/* As live's Drupal title: "<em>Edit Basic page</em> title" */}
                <h1 className="edit-task-title"><em>Edit Basic page</em> {page.title}</h1>
                <ul className="tabs primarya">
                    <li onClick={() => { window.location.href = '/' + page.url_slug; }}>
                        <a style={{ cursor: "pointer" }}>View</a>
                    </li>
                    <li className="active">
                        <a style={{ cursor: "pointer" }}>Edit</a>
                    </li>
                </ul>

                {/* "View changes": the Diff module's table, as the other edit forms show it */}
                {changesSnapshot && originalData && (
                    <div className="diff-table-wrapper">
                        <table className="diff-table">
                            <colgroup>
                                <col className="diff-marker" />
                                <col className="diff-content" />
                                <col className="diff-marker" />
                                <col className="diff-content" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th colSpan="2">Original</th>
                                    <th colSpan="2">Changes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const rows = DIFF_FIELDS.flatMap(([label, field]) =>
                                        diffRows(label, diffText(originalData, field, courseTitle), diffText(changesSnapshot, field, courseTitle)));
                                    return rows.length > 0 ? rows : (
                                        <tr className="no-changes">
                                            <td className="diff-section-title" colSpan="4">No visible changes</td>
                                        </tr>
                                    );
                                })()}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Preview: Drupal's trimmed and full versions of the page */}
                {preview && (
                    <>
                        <div
                            className="messages status"
                            style={{ background: '#f0f8eb url("/basic/web/images/message-ok.png") no-repeat', backgroundPosition: '10px 10px' }}
                        >
                            The trimmed version of your post shows what your post looks like when promoted
                            to the main page or when exported for syndication.
                            <span>
                                {' '}You can insert the delimiter &lt;!--break--&gt; (without the quotes) to
                                fine-tune where your post gets split.
                            </span>
                        </div>
                        <div className="preview-container">
                            <div className="preview-trimmed">
                                <h4 className="preview-heading">
                                    <span className="preview-square"></span> Preview trimmed version
                                </h4>
                                <div className="email-teaser">
                                    <h2 className="email-title">
                                        <a href={'/' + page.url_slug}>{preview.title}</a>
                                    </h2>
                                    <div className="preview-body-content" dangerouslySetInnerHTML={{ __html: preview.trimmed }} />
                                    <div className="read-more-link">
                                        <a href={'/' + page.url_slug}>Read more</a>
                                    </div>
                                </div>
                            </div>
                            <div className="preview-full">
                                <h4 className="preview-heading">
                                    <span className="preview-square"></span> Preview full version
                                </h4>
                                <div className="email-teaser">
                                    <div dangerouslySetInnerHTML={{ __html: preview.body }} />
                                    <div className="email-meta">
                                        {preview.field_meta_title && (
                                            <p><strong>Meta Title:</strong> {preview.field_meta_title}</p>
                                        )}
                                        {preview.field_meta_keywords && (
                                            <p><strong>Meta Keywords:</strong> {preview.field_meta_keywords}</p>
                                        )}
                                        {preview.field_meta_description && (
                                            <p><strong>Meta Description:</strong> {preview.field_meta_description}</p>
                                        )}
                                        {preview.field_url_restriction.length > 0 && (
                                            <p><strong>URL Restriction:</strong> {urlRestrictionLabels(preview.field_url_restriction)}</p>
                                        )}
                                        {preview.field_header_image && (
                                            <p><strong>Header Image:</strong> {preview.field_header_image}</p>
                                        )}
                                        {preview.field_form_switch && (
                                            <p><strong>Form Switch:</strong> {preview.field_form_switch}</p>
                                        )}
                                        {preview.field_parent_course && (
                                            <p><strong>Parent Course:</strong> {courseTitle(preview.field_parent_course)}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* drupal-edit-form: the fields as live's Drupal form shows them (DrupalEditForm.css) */}
                <div className="edit-blog-container drupal-edit-form">
                    {Object.values(errors).some((msg) => !!msg) && (
                        <div className="set-message-error" style={{ background: errorIcon, padding: "10px 10px 10px 50px", width: "90%" }}>
                            <ul>
                                {Object.values(errors).filter((msg) => !!msg).map((msg, index) => (
                                    <li key={index}>{msg}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {backendErrorMessage && (
                        <div className="set-message-error" style={{ background: errorIcon, padding: "10px 10px 10px 50px", width: "90%" }}>
                            {backendErrorMessage}
                        </div>
                    )}

                    {formData && (
                        <form onSubmit={handleSubmit}>
                            <div className="form-item">
                                <label htmlFor="edit-page-title">
                                    Title <span className="form-required" title="This field is required.">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="edit-page-title"
                                    name="title"
                                    size={60}
                                    maxLength={255}
                                    value={formData.title}
                                    onChange={handleChange}
                                    style={errors.title ? { border: '2px solid red' } : {}}
                                />
                            </div>

                            {showSummary && (
                                <div className="form-item">
                                    <label htmlFor="edit-page-summary">
                                        Summary <span>(<a href="#" onClick={(e) => { e.preventDefault(); setShowSummary(false); }}>Hide summary</a>)</span>
                                    </label>
                                    <CkEditor
                                        rows={5}
                                        value={formData.body_summary}
                                        onChange={(html) => setFormData((prev) => ({ ...prev, body_summary: html }))}
                                    />
                                    <div className="description">Leave blank to use trimmed value of full text as the summary.</div>
                                </div>
                            )}

                            <div className="form-item">
                                <label htmlFor="edit-page-body">
                                    Body
                                    {!showSummary && (
                                        <span> (<a href="#" onClick={(e) => { e.preventDefault(); setShowSummary(true); }}>Edit summary</a>)</span>
                                    )}
                                </label>
                                {useRichEditor ? (
                                    <>
                                        <CkEditor
                                            rows={20}
                                            value={formData.body}
                                            onChange={(html) => setFormData((prev) => ({ ...prev, body: html }))}
                                        />
                                        <a href="#" onClick={(e) => { e.preventDefault(); setUseRichEditor(false); }}>
                                            Switch to plain text editor
                                        </a>
                                    </>
                                ) : (
                                    <>
                                        <textarea
                                            id="edit-page-body"
                                            name="body"
                                            rows="20"
                                            value={formData.body}
                                            onChange={handleChange}
                                        />
                                        <div className="grippie" style={grippie}></div>
                                        <a href="#" onClick={(e) => { e.preventDefault(); setUseRichEditor(true); }}>
                                            Switch to rich text editor
                                        </a>
                                    </>
                                )}
                            </div>

                            <div className="form-item">
                                <label htmlFor="edit-page-meta-title">Meta Title</label>
                                <input
                                    type="text"
                                    id="edit-page-meta-title"
                                    name="field_meta_title"
                                    size={60}
                                    maxLength={255}
                                    value={formData.field_meta_title}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="edit-page-meta-keywords">Meta Keywords</label>
                                <input
                                    type="text"
                                    id="edit-page-meta-keywords"
                                    name="field_meta_keywords"
                                    size={60}
                                    maxLength={255}
                                    value={formData.field_meta_keywords}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="edit-page-meta-description">Meta Description</label>
                                <textarea
                                    id="edit-page-meta-description"
                                    name="field_meta_description"
                                    rows={5}
                                    value={formData.field_meta_description}
                                    onChange={handleChange}
                                />
                                <div className="grippie" style={grippie}></div>
                            </div>

                            {/* Drupal's checkboxes: an option label after each box */}
                            <div className="form-item form-type-checkboxes">
                                <label>URL Restriction </label>
                                <div className="form-checkboxes">
                                    {URL_RESTRICTIONS.map((choice, index) => (
                                        <div key={choice.value} className="form-item form-type-checkbox">
                                            <input
                                                type="checkbox"
                                                id={'edit-page-url-restriction-' + index}
                                                className="form-checkbox"
                                                name="field_url_restriction"
                                                value={choice.value}
                                                checked={formData.field_url_restriction.includes(choice.value)}
                                                onChange={handleUrlRestrictionChange}
                                            />{' '}
                                            <label className="option" htmlFor={'edit-page-url-restriction-' + index}>{choice.label} </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="form-item">
                                <label htmlFor="edit-page-header-image">Header Image </label>
                                <input
                                    type="text"
                                    id="edit-page-header-image"
                                    className="text-full form-text"
                                    name="field_header_image"
                                    size={60}
                                    maxLength={100}
                                    value={formData.field_header_image}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="edit-page-form-switch">Form Switch </label>
                                <select
                                    id="edit-page-form-switch"
                                    className="form-select"
                                    name="field_form_switch"
                                    value={formData.field_form_switch}
                                    onChange={handleChange}
                                >
                                    <option value="">- None -</option>
                                    {[...new Set([...FORM_SWITCHES, originalData.field_form_switch].filter(Boolean))].map((choice) => (
                                        <option key={choice} value={choice}>{choice}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-item">
                                <label htmlFor="edit-page-parent-course">Parent Course </label>
                                <select
                                    id="edit-page-parent-course"
                                    className="form-select"
                                    name="field_parent_course"
                                    value={formData.field_parent_course}
                                    onChange={handleChange}
                                >
                                    <option value="">- None -</option>
                                    {parentCourseChoices.map((course) => (
                                        <option key={course.nid} value={course.nid}>{course.title}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-item">
                                <label htmlFor="edit-page-alias">URL alias</label>
                                <input
                                    type="text"
                                    id="edit-page-alias"
                                    name="url_alias"
                                    size={60}
                                    value={formData.url_alias}
                                    onChange={handleChange}
                                />
                                <p className="description" style={{ marginTop: "5px" }}>Optionally specify an alternative URL by which this content can be accessed. For example, type "about" when writing an about page.
                                    Use a relative path and don't add a trailing slash or the URL alias won't work.</p>
                            </div>

                            {/* Live's buttons */}
                            <div className="form-actions form-wrapper">
                                <button type="submit" className="form-submit" disabled={disabled}>{buttonText}</button>{' '}
                                <input type="button" id="edit-preview" value="Preview" className="form-submit" onClick={handlePreviewClick} />{' '}
                                <input type="button" id="edit-task-preview-changes" value="View changes" className="form-submit" onClick={handleViewChangesClick} />{' '}
                                <input type="button" id="edit-delete" value="Delete" className="form-submit" onClick={handleDelete} disabled={disabled} />
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditBasicPage;
